/**
 * The error codes this client acts on (Phase 26).
 *
 * Declared in one place rather than as string literals scattered through
 * components — the same "closed set, described rather than guessed" posture the
 * streams, the report kinds and the block types already take. Two reasons, and
 * the second is the one that pays:
 *
 * 1. A branch on a misspelled code is a branch that never fires, and nothing
 *    about it looks wrong.
 * 2. Because they are all here, a backend test reads this file and asserts that
 *    every one of them is a code the API can actually send. Drift between the
 *    two halves stops being something somebody has to notice.
 *
 * Only the codes the client **branches on** belong here. Everything else is
 * rendered from `error.message`, which the backend writes for a person — a
 * client that hardcoded its own wording for ninety-odd codes would be a second
 * copy of the product's voice, going stale.
 */

/** Codes the backend can send, that this client treats specially. */
export const HANDLED_CODES = [
  /** The body did not validate; `details` carries the per-field messages. */
  'VALIDATION_ERROR',
  /** Throttled; `details.retry_after_seconds` says how long to wait. */
  'RATE_LIMITED',
  /** No session. The app signs the reader out rather than showing a failure. */
  'NOT_AUTHENTICATED',
  /** There is nothing to write to; the editor offers "start a new draft". */
  'NO_OPEN_DRAFT',
  /** A figure inside a good response could not be shown to this reader. */
  'FIGURE_UNAVAILABLE',
  /** A public figure over private rows would have shown too much (Phase 15). */
  'LEAKAGE_REFUSED',
] as const;

export type HandledCode = (typeof HANDLED_CODES)[number];

/**
 * Not from the API: the request never reached it.
 *
 * Kept apart from the codes above on purpose — those are things the server
 * decided, this is the absence of a server — and excluded from the check that
 * every code here is one the API can send, because this one never is.
 */
export const NETWORK_ERROR = 'NETWORK_ERROR';

export function isHandled(code: string): code is HandledCode {
  return (HANDLED_CODES as readonly string[]).includes(code);
}
